import { useEffect, useRef, useState } from 'react';
import { Icon, IconButton } from '../design';
import { useClient } from '../lib/hooks';

/**
 * Tìm trong hội thoại đang mở (tin đã giải mã trên thiết bị này; không phân biệt hoa/thường và dấu). Kết quả mới nhất
 * trước: Enter / nút ▲ sang kết quả cũ hơn, Shift+Enter / nút ▼ sang kết quả mới hơn, Esc đóng. Mỗi lần đổi kết quả gọi
 * `onReveal` để cuộn tới tin (tải thêm lịch sử khi cần).
 */
export function ConversationSearch({ conversationId, onReveal, onClose }: { conversationId: string; onReveal: (messageId: string) => void; onClose: () => void }) {
  const client = useClient();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [searched, setSearched] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setHits([]);
      setSearched(false);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      void client.search(q, { conversationId, limit: 500 }).then((r) => {
        if (!alive) return;
        const ids = r.map((x) => x.message_id);
        setHits(ids);
        setIndex(0);
        setSearched(true);
        if (ids[0]) onReveal(ids[0]);
      });
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // onReveal đổi theo mỗi lần render của cha; chỉ chạy lại khi từ khóa/hội thoại đổi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, conversationId, query]);

  function go(delta: number) {
    if (!hits.length) return;
    const next = (index + delta + hits.length) % hits.length;
    setIndex(next);
    onReveal(hits[next]!);
  }

  return (
    <div className="ws-convsearch" role="search" data-testid="conv-search">
      <Icon name="search" size={16} />
      <input
        ref={input}
        type="search"
        className="ws-convsearch-input"
        placeholder="Tìm trong hội thoại…"
        aria-label="Tìm trong hội thoại"
        data-testid="conv-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            go(e.shiftKey ? -1 : 1);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      />
      <span className="ws-convsearch-count ws-small ws-muted" role="status" data-testid="conv-search-count">
        {!query.trim() ? '' : !searched ? '…' : hits.length ? `${index + 1}/${hits.length}` : 'Không có kết quả'}
      </span>
      <IconButton label="Kết quả cũ hơn" icon="up" size={16} data-testid="conv-search-older" disabled={!hits.length} onClick={() => go(1)} />
      <IconButton label="Kết quả mới hơn" icon="down" size={16} data-testid="conv-search-newer" disabled={!hits.length} onClick={() => go(-1)} />
      <IconButton label="Đóng tìm kiếm" icon="close" size={16} data-testid="conv-search-close" onClick={onClose} />
    </div>
  );
}
